import { useSearchParams } from "react-router-dom";
import type { Article } from "@/lib/api";

interface TopicChipsProps {
  articles: Article[];
  maxTopics?: number;
}

export function TopicChips({ articles, maxTopics = 10 }: TopicChipsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTopic = searchParams.get("topic") || "";

  // Count topic occurrences across all loaded articles.
  const topicCounts = new Map<string, number>();
  for (const article of articles) {
    for (const topic of article.topics || []) {
      topicCounts.set(topic, (topicCounts.get(topic) || 0) + 1);
    }
  }

  // Sort by count descending, take top N.
  const trending = [...topicCounts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, maxTopics);

  if (trending.length === 0) return null;

  const setTopic = (topic: string) => {
    const next = new URLSearchParams(searchParams);
    if (topic && topic !== activeTopic) {
      next.set("topic", topic);
    } else {
      next.delete("topic");
    }
    setSearchParams(next);
  };

  return (
    <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide py-2">
      <span className="shrink-0 text-xs font-body text-beige uppercase tracking-wider">
        Trending
      </span>
      {trending.map(([topic]) => (
        <button
          key={topic}
          onClick={() => setTopic(topic)}
          className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-body transition-colors border ${
            activeTopic === topic
              ? "border-orange bg-orange/10 text-orange"
              : "border-beige/30 text-grey hover:border-orange/50 hover:text-orange"
          }`}
        >
          {topic}
        </button>
      ))}
    </div>
  );
}
